import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { components, paths } from "@breenova/api-client";

type Schemas = components["schemas"];

export type PairSummary = Schemas["PairSummary"];
export type NestSummary = Schemas["NestSummary"];
export type NestDetail = Schemas["NestDetail"];
export type EggDto = Schemas["EggDto"];
export type ChickDto = Schemas["ChickDto"];
export type EggStatus = Schemas["EggStatus"];
export type ChickStatus = Schemas["ChickStatus"];
export type BreedingSeasonDto = Schemas["BreedingSeasonDto"];
export type CalendarEntry = Schemas["GetBreedingCalendarCalendarEntry"];

type JsonBody<P, M extends keyof P> = P[M] extends {
  requestBody: { content: { "application/json": infer B } };
}
  ? B
  : never;

export type CreateSeasonBody = JsonBody<paths["/api/v1/breeding-seasons"], "post">;
export type UpdateSeasonBody = JsonBody<paths["/api/v1/breeding-seasons/{id}"], "put">;
export type CreatePairBody = JsonBody<paths["/api/v1/pairs"], "post">;
export type UpdatePairBody = JsonBody<paths["/api/v1/pairs/{id}"], "put">;

export const breedingKeys = {
  all: ["breeding"] as const,
  seasons: ["breeding", "seasons"] as const,
  pairs: (seasonId?: string) => ["breeding", "pairs", seasonId ?? null] as const,
  nests: (pairId?: string) => ["breeding", "nests", pairId ?? null] as const,
  nest: (id: string) => ["breeding", "nest", id] as const,
  calendar: (from?: string, to?: string) => ["breeding", "calendar", from ?? null, to ?? null] as const,
};

function unwrap<T>(data: T | undefined, error: unknown, what: string): T {
  if (error || data === undefined) {
    throw new Error(`Could not load ${what}.`);
  }

  return data;
}

/** Pulls a readable message out of an RFC 9457 problem-details body. */
export function problemMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const problem = error as { detail?: string; title?: string; errors?: Record<string, string[]> };
    const first = problem.errors ? Object.values(problem.errors).flat()[0] : undefined;

    return first ?? problem.detail ?? problem.title ?? fallback;
  }

  return fallback;
}

/**
 * How a season is written wherever it has to read as one thing — `2026`, or
 * `2026 · indoor`.
 *
 * A breeding year holds as many seasons as the breeder runs: birds kept inside in a heated
 * birdroom go down in January, the outdoor birds in April. The year alone does not identify
 * a season, so every screen that names one goes through here rather than composing its own.
 */
export function seasonLabel(season: { year: number; name: string | null }): string {
  return season.name ? `${season.year} · ${season.name}` : String(season.year);
}

export function useBreedingSeasons() {
  return useQuery({
    queryKey: breedingKeys.seasons,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/breeding-seasons");
      return unwrap(data, error, "your breeding seasons");
    },
  });
}

export function usePairs(seasonId?: string, activeOnly = false) {
  return useQuery({
    queryKey: [...breedingKeys.pairs(seasonId), activeOnly],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/pairs", {
        params: {
          query: {
            ...(seasonId ? { BreedingSeasonId: seasonId } : {}),
            ...(activeOnly ? { ActiveOnly: true } : {}),
            PageSize: 100,
          },
        },
      });

      return unwrap(data, error, "your pairs");
    },
    placeholderData: (previous) => previous,
  });
}

export function useNests(pairId?: string) {
  return useQuery({
    queryKey: breedingKeys.nests(pairId),
    enabled: Boolean(pairId),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/nests", {
        params: { query: pairId ? { PairId: pairId } : {} },
      });

      return unwrap(data, error, "the nests");
    },
  });
}

/**
 * Whether a round is still going.
 *
 * A presentation decision, not a filter the API offers: fledged and failed are both
 * history the moment they are recorded, and everything else is a nest someone still has
 * to look in on.
 */
const RUNNING_STATUSES = new Set<string>(["Building", "Laying", "Incubating", "Hatching"]);

export function isRunningNest(nest: NestSummary): boolean {
  return RUNNING_STATUSES.has(nest.status);
}

/**
 * Every nest, across pairs.
 *
 * Both the home screen and the nests list need the aviary's rounds rather than one
 * pair's, and they need different slices of it — running for the dials, failed for the
 * filter chip. One query, filtered at the call site, so switching a chip does not refetch.
 */
export function useAllNests() {
  return useQuery({
    queryKey: [...breedingKeys.nests(), "all"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/nests", { params: { query: {} } });

      return unwrap(data, error, "the nests").nests;
    },
  });
}

/** The running rounds only — what the home screen's dials show. */
export function useActiveNests() {
  const nests = useAllNests();

  return {
    ...nests,
    data: nests.data?.filter(isRunningNest),
  };
}

export function useNest(id: string | undefined) {
  return useQuery({
    queryKey: breedingKeys.nest(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/nests/{id}", {
        params: { path: { id: id! } },
      });

      return unwrap(data, error, "that nest") as NestDetail;
    },
  });
}

export function useBreedingCalendar(from?: string, to?: string) {
  return useQuery({
    queryKey: breedingKeys.calendar(from, to),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/breeding/calendar", {
        params: { query: { ...(from ? { From: from } : {}), ...(to ? { To: to } : {}) } },
      });

      return unwrap(data, error, "the breeding calendar");
    },
  });
}

/**
 * Every breeding write invalidates the whole breeding tree.
 *
 * Deliberately blunt: adding an egg moves the nest's expected hatch date, which moves the
 * calendar, which changes the pair's fertility rate. Invalidating precisely would mean
 * re-deriving that chain at every call site and getting it wrong somewhere.
 */
function useBreedingMutation<TInput, TOutput>(
  mutationFn: (input: TInput) => Promise<TOutput>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: breedingKeys.all });

      // Ringing a chick creates a bird, so the register is stale too.
      void queryClient.invalidateQueries({ queryKey: ["birds"] });
    },
  });
}

/**
 * Open a season.
 *
 * `makeCurrent` is the whole reason this is worth a screen: the server keeps exactly one
 * current season per breeder and stands the incumbent down as part of the same write, so
 * the caller never has to clear the old one first.
 */
export function useCreateBreedingSeason() {
  return useBreedingMutation(async (body: CreateSeasonBody) => {
    const { data, error } = await api.POST("/api/v1/breeding-seasons", { body });

    if (error || !data) {
      throw new Error(problemMessage(error, "Could not open that season."));
    }

    return data;
  });
}

/**
 * Rename, re-date, close, reopen or make current.
 *
 * The handler merges rather than replaces: a null name or date means "leave it alone",
 * not "clear it". So this sends what the form holds, and the seasons screen does not
 * offer to blank a date it has already saved.
 */
export function useUpdateBreedingSeason() {
  return useBreedingMutation(async ({ id, body }: { id: string; body: UpdateSeasonBody }) => {
    const { data, error } = await api.PUT("/api/v1/breeding-seasons/{id}", {
      params: { path: { id } },
      body,
    });

    if (error || !data) {
      throw new Error(problemMessage(error, "Could not save that season."));
    }

    return data;
  });
}

/**
 * Put a cock and a hen together.
 *
 * The season is the caller's: passing null lands the pair in whichever season is current,
 * which is right from the quick action and wrong from a screen that is showing 2025. The
 * pairs screen therefore sends the season it is displaying.
 *
 * Whether the pairing is legal, and whether it is a mule, are both the server's answer —
 * `PairingRules` owns them, and a screen that re-implemented the sex check would be a
 * second copy to keep in step.
 */
export function useCreatePair() {
  return useBreedingMutation(async (body: CreatePairBody) => {
    const { data, error } = await api.POST("/api/v1/pairs", { body });

    if (error || !data) {
      throw new Error(problemMessage(error, "Could not put that pair together."));
    }

    return data;
  });
}

/**
 * Move, re-date, separate or annotate a pair.
 *
 * Unlike the season update this one replaces: the handler assigns the cage, the
 * separation date and the notes straight across, so a null clears them. Only `pairedOn`
 * merges. Every caller therefore sends the whole pair, not a patch.
 */
export function useUpdatePair() {
  return useBreedingMutation(async ({ id, body }: { id: string; body: UpdatePairBody }) => {
    const { data, error } = await api.PUT("/api/v1/pairs/{id}", {
      params: { path: { id } },
      body,
    });

    if (error || !data) {
      throw new Error(problemMessage(error, "Could not save that pair."));
    }

    return data;
  });
}

/**
 * The server refuses this once the pair has nests — they carry the parentage of every
 * chick it produced — and says to separate it instead. That refusal is the useful part
 * and goes straight on screen.
 */
export function useDeletePair() {
  return useBreedingMutation(async (id: string) => {
    const { data, error } = await api.DELETE("/api/v1/pairs/{id}", {
      params: { path: { id } },
    });

    if (error || !data) {
      throw new Error(problemMessage(error, "Could not remove that pair."));
    }

    return data;
  });
}

export function useAddEgg(nestId: string) {
  return useBreedingMutation(async (input: { laidOn?: string | null }) => {
    const { data, error } = await api.POST("/api/v1/nests/{id}/eggs", {
      params: { path: { id: nestId } },
      body: { nestId, laidOn: input.laidOn ?? null, position: null, status: "Laid", notes: null },
    });

    if (error) {
      throw new Error(problemMessage(error, "Could not add the egg."));
    }

    return data;
  });
}

export function useSetEggStatus() {
  return useBreedingMutation(async (input: { eggId: string; status: EggStatus; on?: string }) => {
    const { data, error } = await api.PATCH("/api/v1/eggs/{id}/status", {
      params: { path: { id: input.eggId } },
      body: { id: input.eggId, status: input.status, on: input.on ?? null },
    });

    if (error) {
      throw new Error(problemMessage(error, "Could not update the egg."));
    }

    return data;
  });
}

export function useFosterEgg() {
  return useBreedingMutation(async (input: { eggId: string; fosterNestId: string | null }) => {
    const { data, error } = await api.POST("/api/v1/eggs/{id}/foster", {
      params: { path: { id: input.eggId } },
      body: { id: input.eggId, fosterNestId: input.fosterNestId, on: null },
    });

    if (error) {
      throw new Error(problemMessage(error, "Could not foster the egg."));
    }

    return data;
  });
}

export function useRingChick() {
  return useBreedingMutation(async (input: { chickId: string; ringNumber?: string }) => {
    const { data, error } = await api.POST("/api/v1/chicks/{id}/ring", {
      params: { path: { id: input.chickId } },
      body: {
        id: input.chickId,
        ringNumber: input.ringNumber ?? null,
        ringBatchId: null,
        ringedOn: null,
        locationId: null,
      },
    });

    if (error) {
      throw new Error(problemMessage(error, "Could not ring the chick."));
    }

    return data;
  });
}

export function useSetChickStatus() {
  return useBreedingMutation(async (input: { chickId: string; status: ChickStatus }) => {
    const { data, error } = await api.PATCH("/api/v1/chicks/{id}/status", {
      params: { path: { id: input.chickId } },
      body: { id: input.chickId, status: input.status, on: null, weightGrams: null },
    });

    if (error) {
      throw new Error(problemMessage(error, "Could not update the chick."));
    }

    return data;
  });
}

export function useCreateNest() {
  return useBreedingMutation(async (input: { pairId: string }) => {
    const { data, error } = await api.POST("/api/v1/nests", {
      body: {
        pairId: input.pairId,
        locationId: null,
        startedOn: null,
        // Omitted on purpose: the round is assigned server-side per pair per season.
        round: null,
        notes: null,
      },
    });

    if (error) {
      throw new Error(problemMessage(error, "Could not start the round."));
    }

    return data;
  });
}

/**
 * How far through incubation a clutch is, 0..1.
 *
 * Counted from the first egg, matching the expected-hatch rule. Returns null when there
 * is nothing to count from — a bar at zero would claim a clutch is being sat on.
 */
export function incubationProgress(
  firstEggOn: string | null | undefined,
  expectedHatchOn: string | null | undefined,
  today: string,
): { progress: number; day: number; total: number } | null {
  if (!firstEggOn || !expectedHatchOn) {
    return null;
  }

  const start = Date.parse(`${firstEggOn}T00:00:00Z`);
  const due = Date.parse(`${expectedHatchOn}T00:00:00Z`);
  const now = Date.parse(`${today}T00:00:00Z`);

  const total = Math.round((due - start) / 86_400_000);
  if (total <= 0) {
    return null;
  }

  const day = Math.round((now - start) / 86_400_000);

  return { progress: Math.min(1, Math.max(0, day / total)), day, total };
}
